import { escapeHtml, getArtistColor, getInitial, lazyLoadImage } from '@utils/index';
import type { Song, Artist, Album, Playlist } from '@/types';

// Safe DOM element creation and manipulation

export function createElement<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  props: Partial<Omit<HTMLElementTagNameMap[K], 'style'>> & { style?: string } & Record<string, any> = {},
  ...children: (string | Node)[]
): HTMLElementTagNameMap[K] {
  const element = document.createElement(tag);

  Object.entries(props).forEach(([key, value]) => {
    if (key === 'class') {
      element.className = value as string;
    } else if (key === 'style' && typeof value === 'string') {
      element.setAttribute('style', value);
    } else if (key.startsWith('data-') || key.startsWith('aria-')) {
      element.setAttribute(key, String(value));
    } else {
      (element as any)[key] = value;
    }
  });

  children.forEach((child) => {
    if (typeof child === 'string') {
      element.appendChild(document.createTextNode(child));
    } else {
      element.appendChild(child);
    }
  });

  return element;
}

export function renderSongItem(
  song: Song,
  onClick: () => void,
  options: { showThumbnail?: boolean; showArtist?: boolean; showFavorite?: boolean; isFavorite?: boolean; onFavoriteToggle?: (videoId: string) => void } = {}
): HTMLElement {
  const { showThumbnail = true, showArtist = true, showFavorite = true, isFavorite = false, onFavoriteToggle } = options;

  const item = createElement('div', {
    class: 'list-item',
    role: 'button',
    tabIndex: 0,
    'aria-label': `Play ${song.title} by ${song.artist}`,
    'data-video-id': song.videoId,
  });

  if (showThumbnail) {
    const thumb = createElement('div', { class: 'list-thumb' });

    if (song.thumbnail) {
      const img = createElement('img', {
        alt: song.title,
        loading: 'lazy',
        'data-src': song.thumbnail,
      }) as HTMLImageElement;
      lazyLoadImage(img);
      thumb.appendChild(img);
    } else {
      thumb.innerHTML = `<div style="width:100%;height:100%;background:linear-gradient(135deg,var(--primary),var(--primary-dark));display:flex;align-items:center;justify-content:center;font-size:20px;">🎵</div>`;
    }

    item.appendChild(thumb);
  }

  const info = createElement('div', { class: 'list-info' });

  const title = createElement('div', { class: 'list-title' });
  title.textContent = song.title;
  info.appendChild(title);

  if (showArtist) {
    const artist = createElement('div', { class: 'list-sub' });
    artist.textContent = song.artist;
    info.appendChild(artist);
  }

  item.appendChild(info);

  // Favorite button
  if (showFavorite) {
    const favBtn = createElement('button', {
      class: `icon-btn favorite-btn ${isFavorite ? 'active' : ''}`,
      'aria-label': isFavorite ? 'Remove from favorites' : 'Add to favorites',
      'data-video-id': song.videoId,
    });
    favBtn.innerHTML = isFavorite
      ? '<svg viewBox="0 0 24 24"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/></svg>'
      : '<svg viewBox="0 0 24 24"><path d="M16.5 3c-1.74 0-3.41.81-4.5 2.09C10.91 3.81 9.24 3 7.5 3 4.42 3 2 5.42 2 8.5c0 3.78 3.4 6.86 8.55 11.54L12 21.35l1.45-1.32C18.6 15.36 22 12.28 22 8.5 22 5.42 19.58 3 16.5 3zm-4.4 15.55l-.1.1-.1-.1C7.14 14.24 4 11.39 4 8.5 4 6.5 5.5 5 7.5 5c1.54 0 3.04.99 3.57 2.36h1.87C13.46 5.99 14.96 5 16.5 5c2 0 3.5 1.5 3.5 3.5 0 2.89-3.14 5.74-7.9 10.05z"/></svg>';

    favBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      if (onFavoriteToggle) {
        onFavoriteToggle(song.videoId);
      }
      // Toggle active state visually
      favBtn.classList.toggle('active');
      const isNowFavorite = favBtn.classList.contains('active');
      favBtn.innerHTML = isNowFavorite
        ? '<svg viewBox="0 0 24 24"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/></svg>'
        : '<svg viewBox="0 0 24 24"><path d="M16.5 3c-1.74 0-3.41.81-4.5 2.09C10.91 3.81 9.24 3 7.5 3 4.42 3 2 5.42 2 8.5c0 3.78 3.4 6.86 8.55 11.54L12 21.35l1.45-1.32C18.6 15.36 22 12.28 22 8.5 22 5.42 19.58 3 16.5 3zm-4.4 15.55l-.1.1-.1-.1C7.14 14.24 4 11.39 4 8.5 4 6.5 5.5 5 7.5 5c1.54 0 3.04.99 3.57 2.36h1.87C13.46 5.99 14.96 5 16.5 5c2 0 3.5 1.5 3.5 3.5 0 2.89-3.14 5.74-7.9 10.05z"/></svg>';
      favBtn.setAttribute('aria-label', isNowFavorite ? 'Remove from favorites' : 'Add to favorites');
    });

    item.appendChild(favBtn);
  }

  // Click handler for whole item (except favorite button)
  item.addEventListener('click', (e) => {
    // Don't trigger if clicking on favorite button
    const target = e.target as HTMLElement;
    if (target.closest('.favorite-btn')) return;
    onClick();
  });

  // Keyboard support
  item.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick();
    }
  });

  return item;
}

export function renderArtistCard(
  artist: Artist,
  onClick: () => void,
  options: { onLoadThumbnail?: (artistId: string, artistName: string) => Promise<string | null> } = {}
): HTMLElement {
  const card = createElement('div', {
    class: 'artist-card',
    role: 'button',
    tabIndex: 0,
    'aria-label': `View ${artist.name}`,
  });

  // Simple click handler - works on both desktop and mobile
  card.addEventListener('click', (e) => {
    e.preventDefault();
    console.log('🎵 Artist card clicked:', artist.name);
    onClick();
  });

  const [color1, color2] = getArtistColor(artist.name);
  const initial = getInitial(artist.name);

  const avatar = createElement('div', { class: 'artist-avatar' });
  const artistId = artist.id || artist.link || '';

  if (artist.thumbnail) {
    const img = createElement('img', {
      alt: artist.name,
      loading: 'lazy',
      'data-src': artist.thumbnail,
    }) as HTMLImageElement;
    lazyLoadImage(img);
    avatar.appendChild(img);
  } else {
    // Show placeholder initially
    avatar.innerHTML = `<div class="artist-avatar-placeholder" style="background:linear-gradient(135deg, ${color1}, ${color2});">${escapeHtml(initial)}</div>`;

    // Fetch thumbnail asynchronously if callback provided
    if (options.onLoadThumbnail && artistId) {
      options.onLoadThumbnail(artistId, artist.name).then((thumbnail) => {
        if (thumbnail) {
          const img = createElement('img', {
            alt: artist.name,
            loading: 'lazy',
          }) as HTMLImageElement;
          img.src = thumbnail;
          avatar.innerHTML = '';
          avatar.appendChild(img);
        }
      });
    }
  }
  card.appendChild(avatar);

  const name = createElement('div', { class: 'artist-name' });
  name.textContent = artist.name;
  card.appendChild(name);

  const subtitle = createElement('div', { class: 'artist-subtitle' });
  subtitle.textContent = 'Artist';
  card.appendChild(subtitle);

  // Keyboard support
  card.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick();
    }
  });

  return card;
}

export function renderAlbumCard(
  album: Album,
  onClick: () => void
): HTMLElement {
  const card = createElement('div', {
    class: 'grid-item',
    role: 'button',
    tabIndex: 0,
    'aria-label': `View album ${album.title}`,
  });

  // Add click handler directly
  card.addEventListener('click', () => {
    console.log('💿 Album card clicked:', album.title);
    onClick();
  });

  const thumb = createElement('div', { class: 'thumb' });

  if (album.thumbnail) {
    const img = createElement('img', {
      alt: album.title,
      loading: 'lazy',
      'data-src': album.thumbnail,
    }) as HTMLImageElement;
    lazyLoadImage(img);
    thumb.appendChild(img);
  } else {
    const [color1, color2] = getArtistColor(album.artist);
    thumb.innerHTML = `<div style="width:100%;height:100%;background:linear-gradient(135deg,${color1},${color2});display:flex;align-items:center;justify-content:center;font-size:32px;">💿</div>`;
  }

  card.appendChild(thumb);

  const title = createElement('div', { class: 'grid-title' });
  title.textContent = album.title;
  card.appendChild(title);

  const sub = createElement('div', { class: 'grid-sub' });
  sub.textContent = 'Album';
  card.appendChild(sub);

  // Keyboard support
  card.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick();
    }
  });

  return card;
}

export function renderPlaylistCard(
  playlist: Playlist,
  onClick: () => void
): HTMLElement {
  const card = createElement('div', {
    class: 'grid-item',
    role: 'button',
    tabIndex: 0,
    'aria-label': `Open playlist ${playlist.name}`,
  });

  // Add click handler directly
  card.addEventListener('click', () => {
    console.log('📝 Playlist card clicked:', playlist.name);
    onClick();
  });

  const thumb = createElement('div', { class: 'thumb' });

  if (playlist.cover) {
    const img = createElement('img', {
      alt: playlist.name,
      loading: 'lazy',
      'data-src': playlist.cover,
    }) as HTMLImageElement;
    lazyLoadImage(img);
    thumb.appendChild(img);
  } else {
    // Generate cover from first 4 songs
    const songs = playlist.songs.slice(0, 4);
    if (songs.length > 0) {
      const grid = createElement('div', {
        style: 'display:grid;grid-template-columns:1fr 1fr;gap:2px;width:100%;height:100%;',
      });

      songs.forEach((song) => {
        const cell = createElement('div', {
          style: 'background:var(--surface-container);overflow:hidden;',
        });
        if (song.thumbnail) {
          const img = createElement('img', {
            src: song.thumbnail,
            alt: '',
            style: 'width:100%;height:100%;object-fit:cover;',
          });
          cell.appendChild(img);
        }
        grid.appendChild(cell);
      });

      thumb.appendChild(grid);
    } else {
      thumb.innerHTML = `<div style="width:100%;height:100%;background:linear-gradient(135deg,var(--primary),var(--primary-dark));display:flex;align-items:center;justify-content:center;font-size:32px;">🎵</div>`;
    }
  }

  card.appendChild(thumb);

  const title = createElement('div', { class: 'grid-title' });
  title.textContent = playlist.name;
  card.appendChild(title);

  const sub = createElement('div', { class: 'grid-sub' });
  sub.textContent = `${playlist.songs.length} songs`;
  card.appendChild(sub);

  // Keyboard support
  card.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick();
    }
  });

  return card;
}

export function renderLoadingSkeleton(type: 'list' | 'grid', count = 10): HTMLElement {
  const container = createElement('div', { class: 'skeleton-container' });

  for (let i = 0; i < count; i++) {
    if (type === 'list') {
      const item = createElement('div', { class: 'list-item skeleton' });
      item.innerHTML = `
        <div class="list-thumb skeleton-box"></div>
        <div class="list-info">
          <div class="skeleton-line" style="width:70%;margin-bottom:8px;"></div>
          <div class="skeleton-line" style="width:50%;"></div>
        </div>
      `;
      container.appendChild(item);
    } else {
      const item = createElement('div', { class: 'grid-item skeleton' });
      item.innerHTML = `
        <div class="thumb skeleton-box"></div>
        <div class="skeleton-line" style="width:80%;margin:8px 0 4px;"></div>
        <div class="skeleton-line" style="width:60%;"></div>
      `;
      container.appendChild(item);
    }
  }

  return container;
}

export function renderEmptyState(
  icon: string,
  title: string,
  text: string,
  action?: { label: string; onClick: () => void }
): HTMLElement {
  const empty = createElement('div', { class: 'empty' });

  const iconEl = createElement('div', { class: 'empty-icon' });
  iconEl.textContent = icon;
  empty.appendChild(iconEl);

  const titleEl = createElement('div', { class: 'empty-title' });
  titleEl.textContent = title;
  empty.appendChild(titleEl);

  const textEl = createElement('div', { class: 'empty-text' });
  textEl.textContent = text;
  empty.appendChild(textEl);

  if (action) {
    const button = createElement('button', {
      class: 'btn btn-primary',
      onclick: action.onClick,
    });
    button.textContent = action.label;
    empty.appendChild(button);
  }

  return empty;
}

export function renderError(message: string, retry?: () => void): HTMLElement {
  const error = createElement('div', { class: 'empty' });

  const icon = createElement('div', { class: 'empty-icon' });
  icon.textContent = '😕';
  error.appendChild(icon);

  const title = createElement('div', { class: 'empty-title' });
  title.textContent = 'Oops!';
  error.appendChild(title);

  const text = createElement('div', { class: 'empty-text' });
  text.textContent = message;
  error.appendChild(text);

  if (retry) {
    const button = createElement('button', {
      class: 'btn btn-primary',
      onclick: retry,
    });
    button.textContent = 'Try Again';
    error.appendChild(button);
  }

  return error;
}

// Clear and replace element content safely
export function replaceContent(element: HTMLElement, ...children: Node[]): void {
  element.innerHTML = '';
  children.forEach((child) => element.appendChild(child));
}

// Append multiple children
export function appendChildren(element: HTMLElement, ...children: Node[]): void {
  children.forEach((child) => element.appendChild(child));
}
